/**
 * workspace-directory.ts  (Sprint DATA-01C)
 *
 * Sovereign Workspace Directory.
 * Tracks validated workspace members, their roles, and capabilities.
 * Enforces principal separation between assignable human members and compute-only nodes.
 */

export interface WorkspaceMember {
  principalId: string;
  displayName: string;
  hnsName?: string;
  role: 'admin' | 'member' | 'compute-only';
  status: 'active' | 'offline' | 'revoked';
  workspaceIds: readonly string[];
  canReceiveAssignments: boolean; // false for compute-only and revoked
  canBeMentioned: boolean;        // false for revoked
}

export interface WorkspaceDirectoryStorage {
  load(): WorkspaceMember[];
  save(members: WorkspaceMember[]): void;
}

export interface WorkspaceDirectory {
  getMembers(workspaceId: string): readonly WorkspaceMember[];
  getAssignees(workspaceId: string): readonly WorkspaceMember[];
  getMentionable(workspaceId: string): readonly WorkspaceMember[];
  getComputeNodes(workspaceId: string): readonly WorkspaceMember[];
  addMember(member: WorkspaceMember): void;
  revokeMember(principalId: string): void;
  updateStatus(principalId: string, status: WorkspaceMember['status']): void;
}

export class InMemoryWorkspaceDirectory implements WorkspaceDirectory {
  private members: WorkspaceMember[];

  constructor(private readonly storage: WorkspaceDirectoryStorage) {
    this.members = storage.load();
  }

  getMembers(workspaceId: string): readonly WorkspaceMember[] {
    return this.members.filter(
      (m) => m.status !== 'revoked' && (m.workspaceIds.includes(workspaceId) || m.workspaceIds.includes('*')),
    );
  }

  getAssignees(workspaceId: string): readonly WorkspaceMember[] {
    return this.getMembers(workspaceId).filter((m) => m.canReceiveAssignments);
  }

  getMentionable(workspaceId: string): readonly WorkspaceMember[] {
    return this.getMembers(workspaceId).filter((m) => m.canBeMentioned);
  }

  getComputeNodes(workspaceId: string): readonly WorkspaceMember[] {
    return this.members.filter(
      (m) => m.role === 'compute-only' && (m.workspaceIds.includes(workspaceId) || m.workspaceIds.includes('*')),
    );
  }

  addMember(member: WorkspaceMember): void {
    const existingIdx = this.members.findIndex((m) => m.principalId === member.principalId);
    if (existingIdx >= 0) {
      this.members[existingIdx] = member;
    } else {
      this.members.push(member);
    }
    this.storage.save(this.members);
  }

  revokeMember(principalId: string): void {
    this.members = this.members.map((m) =>
      m.principalId === principalId
        ? { ...m, status: 'revoked' as const, canReceiveAssignments: false, canBeMentioned: false }
        : m,
    );
    this.storage.save(this.members);
  }

  updateStatus(principalId: string, status: WorkspaceMember['status']): void {
    this.members = this.members.map((m) =>
      m.principalId === principalId ? { ...m, status } : m,
    );
    this.storage.save(this.members);
  }
}

export class LocalStorageWorkspaceDirectoryStorage implements WorkspaceDirectoryStorage {
  private static readonly KEY = 'workspace_members_v1';

  load(): WorkspaceMember[] {
    if (typeof window === 'undefined') return [];
    try {
      const raw = localStorage.getItem(LocalStorageWorkspaceDirectoryStorage.KEY);
      return raw ? (JSON.parse(raw) as WorkspaceMember[]) : [];
    } catch {
      return [];
    }
  }

  save(members: WorkspaceMember[]): void {
    if (typeof window === 'undefined') return;
    try {
      localStorage.setItem(LocalStorageWorkspaceDirectoryStorage.KEY, JSON.stringify(members));
    } catch {}
  }
}

export const globalWorkspaceDirectory = new InMemoryWorkspaceDirectory(
  new LocalStorageWorkspaceDirectoryStorage(),
);
